import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import withStyles from "@mui/styles/withStyles";
import { withTranslation } from "react-i18next";
import actions from "../../actions";
import styles from "../Signin/styles";
import ResetPassword from "../ResetPassword/ResetPassword";
import UpdateMemberInfo from "../AcceptInvitation/UpdateMemberInfo";
import { ANLoadingScreen } from "@answersnow/pkg-theme";
import cookie from "react-cookies";
import { getCookieDomain } from "../../utils/cookieDomain";
import {
  getPasswordChange,
  getUserAttributes,
  getLoginInvitation,
  getInvitationError,
  getInvitationCompletion,
  getInvitationCompletionError,
  getGroups,
} from "../../selectors";

const mapStateToProps = (state) => ({
  passwordChange: getPasswordChange(state),
  userAttributes: getUserAttributes(state),
  loginInvitation: getLoginInvitation(state),
  invitationError: getInvitationError(state),
  invitationCompletion: getInvitationCompletion(state),
  invitationCompletionError: getInvitationCompletionError(state),
  userGroups: getGroups(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      changeUserPassword: actions.changeUserPassword,
      getLoginInvitation: actions.getLoginInvitation,
      completeInvitation: actions.completeInvitation,
      setUserAttributes: actions.setUserAttributes,
      setPageDetails: actions.setPageDetails,
      agreeToPolicies: actions.agreeToPolicies,
    },
    dispatch
  );

function getInitialState(props) {
  return {
    username: props.userAttributes.email || "",
    currentPassword: "",
    verificationCode: "ignoreme",
    // Derive from the guid param so it's correct on the FIRST render — otherwise the
    // change-password variant (with a current-password field) would flash before
    // componentDidMount flips it.
    invitationSignin: Boolean(props.match?.params?.guid),
    step: "setPassword",
  };
}

class ForcePasswordContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ...getInitialState(this.props),
    };
  }

  componentDidMount() {
    this.props.setPageDetails({
      pageName: this.props.t("auth:resetPassword.pageTitle"),
    });

    if (!this.props.passwordChange && !this.props.match.params.guid) {
      this.props.history.push("/dashboard");
    }
    if (this.props.match.params.guid) {
      // invitationSignin is already derived from the guid in the constructor.
      this.props.getLoginInvitation(this.props.match.params.guid);
    }
  }

  componentDidUpdate(prevProps) {
    // Invitation details loaded — populate the email for display. No client-side
    // Cognito sign-in: the backend establishes the session on completion.
    if (!prevProps.loginInvitation && this.props.loginInvitation) {
      this.props.setUserAttributes({
        username: this.props.loginInvitation.email,
      });
    }

    // Setup-link completion succeeded (backend set the password + established the
    // session). Route the now-authenticated user by their role.
    if (!prevProps.invitationCompletion && this.props.invitationCompletion) {
      const { authorityName, userId, clientId, firstName, lastName, phoneNumber } =
        this.props.invitationCompletion;

      if (authorityName === "ROLE_PARENT") {
        this.props.agreeToPolicies({ userId });
        const expires = new Date();
        expires.setTime(Date.now() + 1000 * 60 * 60 * 24 * 14);
        cookie.save("userId", userId, { path: "/", domain: getCookieDomain(), expires });
        this.setState({
          step: "updateMemberInfo",
          completion: { userId, clientId, firstName, lastName, phoneNumber },
        });
      } else {
        this.props.history.push("/dashboard");
      }
    }

    // Logged-in force-password-change (driven by the /auth/me flag): no
    // invitation guid, so just return the now-unblocked user to the dashboard
    // once the flag clears.
    if (
      prevProps.passwordChange &&
      !this.props.passwordChange &&
      !this.props.match.params.guid
    ) {
      this.props.history.push("/dashboard");
    }
  }

  onChange = (name) => (e) => {
    const { checked, type, value } = e.target;
    const val = type === "checkbox" ? checked : value;

    this.setState({ [name]: val });
  };

  onSubmit = (values) => {
    // Logged-in force-password-change (no invitation guid): the user has a
    // valid session, so rotate the password through the backend
    // (PUT /users/me/change-password) rather than client-side Cognito.
    this.props.changeUserPassword({
      ...this.state,
      ...values,
    });
  };

  onSubmitInvitationLogin = (values) => {
    // Setup-link path: the backend sets the password, establishes the session,
    // and marks the invitation used — no client-side Cognito.
    this.props.completeInvitation({
      guid: this.props.match.params.guid,
      password: values.password,
    });
  };

  render() {
    const { classes, loginInvitation, invitationError } = this.props;

    // Invitation (setup-link) flow: the guid is validated by an-api via an async
    // fetch. Until that resolves — into either a valid invitation or an error — show
    // a loader instead of the set-password form, so an arbitrary/invalid /setup/:guid
    // never briefly renders a fillable form. The non-guid force-change flow has no
    // fetch and falls straight through.
    if (this.state.invitationSignin && !loginInvitation && !invitationError) {
      return (
        <div className={classes.container}>
          <ANLoadingScreen />
        </div>
      );
    }

    if (this.state.step === "updateMemberInfo") {
      const completion = this.state.completion || {};
      return (
        <UpdateMemberInfo
          email={loginInvitation?.email}
          userId={completion.userId}
          clientId={completion.clientId}
          isIntakeFlow={true}
          initialData={{
            firstName: completion.firstName || "",
            lastName: completion.lastName || "",
            phoneNumber: completion.phoneNumber || "",
          }}
        />
      );
    }

    return (
      <div className={classes.container}>
        <ResetPassword
          {...this.props}
          {...this.state}
          onChange={this.onChange}
          onSubmit={this.onSubmit}
          onSubmitInvitationLogin={this.onSubmitInvitationLogin}
          change={true}
          showVersion={false}
        />
      </div>
    );
  }
}

export default withStyles(styles)(
  withTranslation(["auth"])(connect(mapStateToProps, mapDispatchToProps)(ForcePasswordContainer))
);
